
  import { ExportLicensesCommand, LicenseDto, ApplicationRoleType } from '@/apiclient/apiclient_generated';
  import ModalComponent from '@/components/ModalComponent.vue';
  import FileService from '@/services/file.service';
  import { NotificationService } from '@/services/notification-service';
  import { AccountModule, AccountStore } from '@/store/account';
  import { LicenseModule, LicenseStore } from '@/store/license';
  import { NavigationModule, NavigationStore } from '@/store/navigation';
  import LicenseStoreState from '@/types/LicenseStoreState';
  import Routes from '@/types/Routes';
  import { FilterMatchMode, FilterService } from 'primevue/api';
  import ProgressBar from 'primevue/progressbar';
  import { ref } from 'vue';
  import { Options, Vue } from 'vue-class-component';
  import { copyText } from 'vue3-clipboard';
  import CreateUpdateLicense from './CreateUpdateLicense.vue';
  import SendLicenseEmail from './SendLicenseEmail.vue';

  @Options({
    components: {
      CreateUpdateLicense,
      ModalComponent,
      SendLicenseEmail
    }
  })
  export default class LicenseManagement extends Vue {
    private licenseStore: LicenseModule = LicenseStore;
    private accountStore: AccountModule = AccountStore;
    private navigationStore: NavigationModule = NavigationStore;

    showModal = false;
    showEmailModal = false;
    selectedLicense: any = ref();
    loading = false;
    private rows = 10;
    selectedAccount: number = null;
    mobileSearchTerm = '';
    license: any = ref({});

    licenseTypeOptions = ['Trial', 'Customer', 'Corporate'];

    globalFilter = ['product', 'type', 'key'];

    filters = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      product: { value: null, matchMode: FilterMatchMode.CONTAINS },
      key: { value: null, matchMode: FilterMatchMode.CONTAINS },
      type: { value: null, matchMode: FilterMatchMode.IN },
      licenseDomain: { value: null, matchMode: FilterMatchMode.CONTAINS },
      createdDate: { value: null, matchMode: 'dateRange' },
      expiryDate: { value: null, matchMode: 'dateRange' }
    });

    rowClass() {
      return 'cursor-pointer';
    }

    get selectableAccounts() {
      return this.accountStore.allAccounts;
    }

    get selectedAccountDto() {
      return this.selectableAccounts.find((t) => t.id === this.selectedAccount);
    }

    get items() {
      return this.licenseStore.selectedAccountLicenses;
    }

    get totalRecords() {
      return this.licenseStore.selectedAccountLicenses.length;
    }

    get mobileFilteredItems() {
      const sourceItems = this.items || [];
      const searchText = (this.mobileSearchTerm || '').trim().toLowerCase();

      if (!searchText) {
        return sourceItems;
      }

      return sourceItems.filter((licenseItem) => {
        const searchable = [licenseItem.product, licenseItem.key, licenseItem.type, licenseItem.licenseDomain]
          .filter((v) => !!v)
          .join(' ')
          .toLowerCase();

        return searchable.includes(searchText);
      });
    }

    usagePercent(licenseItem) {
      if (!licenseItem || !licenseItem.maximumActivations) {
        return 0;
      }

      const value = (licenseItem.activationsCount / licenseItem.maximumActivations) * 100;
      return Math.max(0, Math.min(100, Math.round(value)));
    }

    formatDateValue(value) {
      if (!value) {
        return '-';
      }

      const parsedDate = value instanceof Date ? value : new Date(value);
      if (isNaN(parsedDate.getTime())) {
        return '-';
      }

      return parsedDate.toDateString();
    }

    private sanitizeTableState(stateKey: string, defaultRows = 10) {
      try {
        const raw = localStorage.getItem(stateKey);
        if (raw) {
          const state = JSON.parse(raw);
          if (!state.rows || state.rows <= 0) {
            state.rows = defaultRows;
          }
          // Always clear stored filters — the component owns the filter schema.
          delete state.filters;
          localStorage.setItem(stateKey, JSON.stringify(state));
        }
      } catch {
        localStorage.removeItem(stateKey);
      }
    }

    beforeMount() {
      FilterService.register('dateRange', (value: Date, filter: Date[]) => {
        if (!filter || filter.length === 0) return true;
        const [from, to] = filter;
        if (!from) return true;
        const date = new Date(value);
        date.setHours(0, 0, 0, 0);
        const fromDate = new Date(from);
        fromDate.setHours(0, 0, 0, 0);
        if (!to) return date >= fromDate;
        const toDate = new Date(to);
        toDate.setHours(23, 59, 59, 999);
        return date >= fromDate && date <= toDate;
      });
      this.sanitizeTableState('license-table-state');
    }

    async mounted() {
      this.license = null;
      this.selectedLicense = null;
      this.licenseStore.setAccountLicenses([]);

      await this.accountStore.getAllUsers();

      if (this.selectedAccountId) {
        this.updateMenuActiveState();

        this.selectedAccount = +this.selectedAccountId;

        await this.loadLicenses(this.selectedAccount);
      } else if (this.accountStore.isCustomer) {
        this.updateMenuActiveState();
        this.selectedAccount = +this.accountStore.userData.id;
        await this.loadLicenses(this.selectedAccount);
      }

      this.markDefaultLicense();
    }

    markDefaultLicense() {
      if (this.items.length) {
        this.selectedLicense = this.items[0];
        // this.updateLicenseStoreState();
      }
    }

    get selectedAccountId() {
      const storeState = this.licenseStore.storeState;
      return this.$route.query.account || storeState.selectedAccount;
    }

    updateLicenseStoreState() {
      const params = {
        selectedAccount: this.selectedAccount
      } as LicenseStoreState;

      this.licenseStore.updateStoreState(params);
    }

    updateMenuActiveState() {
      const params = {
        isAdmin: this.accountStore.isAdmin,
        route: this.$route.name
      };

      this.navigationStore.initMenuAction(params);
    }

    sendLicenseEmail() {
      this.showEmailModal = true;
      this.selectedLicense;
    }

    copyLicenseKey() {
      const license = this.selectedLicense;

      this.copy(license.key, 'Key was copied.');
    }

    copyLicenseHash() {
      const license = this.selectedLicense;

      this.copy(license.hash, 'Hash was copied.');
    }

    createLicense() {
      this.license = {} as LicenseDto;
      this.showModal = true;
    }

    modifyLicenseFromGrid(data) {
      this.selectedLicense = data;
      this.modifyLicense();
    }

    modifyLicense() {
      this.license = this.selectedLicense;

      this.showModal = true;
    }

    licenseActivationsAction() {
      const query = {
        account: this.selectedAccountId,
        license: this.selectedLicense.id
      };

      this.$router.push({ path: Routes.management.activations.path, query });
    }

    async deleteLicenseFromGrid(license, $event) {
      this.selectedLicense = license;

      await this.deleteLicense($event);
    }

    async deleteLicense(event) {
      const targetLicenseId = this.selectedLicense.id;

      this.$confirm.require({
        target: event.currentTarget,
        message: 'Are you sure you want to delete selected license?',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          const deleteResult = await this.licenseStore.deleteLicense(targetLicenseId);
          if (deleteResult) {
            this.resetSelectedLicenses();
          }
        },
        reject: () => {
          //callback to execute when user rejects the action
        }
      });
    }

    private copy(value, message) {
      copyText(value, undefined, (error, event) => {
        if (error) {
          NotificationService.NotifyError(error);
        } else {
          NotificationService.NotifySuccess(message);
        }
      });
    }

    async processFilterUpdate() {
      if (this.selectedAccount) {
        await this.loadLicenses(this.selectedAccount);
      } else {
        this.resetSelectedLicenses();
        this.licenseStore.setAccountLicenses([]);
      }
      this.markDefaultLicense();
    }

    async loadLicenses(accountId) {
      this.loading = true;
      await this.licenseStore.getAccountLicenses(accountId);
      this.loading = false;
    }

    licenseDblClicked(row) {
      if (this.accountStore.isCustomer) {
        return;
      }

      const params = {
        account: this.selectedAccount,
        license: row.data.id
      };
      this.$router.push({ path: Routes.management.activations.path, query: params });
    }

    async exportLicense(license) {
      const command = {
        licenseIds: [license.id]
      } as ExportLicensesCommand;

      this._exportLicenses(command);
    }

    async exportLicenses() {
      const exportCommand = {
        licenseIds: this.items.map((l) => l.id)
      } as ExportLicensesCommand;

      await this._exportLicenses(exportCommand);
    }

    async _exportLicenses(command: ExportLicensesCommand) {
      const file = await this.licenseStore.export(command);

      if (file) {
        FileService.downloadFile(file.data, file.fileName);
      }
    }

    async processSaveLicense() {
      this.showModal = false;
      this.resetSelectedLicenses();
      await this.loadLicenses(this.selectedAccount);
    }
    processCancel() {
      this.showModal = false;
    }

    resetSelectedLicenses() {
      this.selectedLicense = null;
    }

    get invalidLicenseFormToDelete() {
      return !this.selectedLicense.id;
    }

    private onCopy() {
      NotificationService.NotifySuccess('Key was copied.');
    }
  }
